import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle, TrendingUp, Globe, Calendar, Users, Package, Loader2,
  Send, ShieldAlert, Clock, ChevronDown, ChevronRight, Zap, BarChart3, Truck,
  ShoppingBag, ArrowUp, ArrowDown, Minus,
} from 'lucide-react';
import ItemCodeLabel from '../components/ItemCodeLabel';
import DataIssuesBanner from '../components/DataIssuesBanner';
import DataGrid from '../components/DataGrid';
import { DrillCell } from '../components/DrillPanel';

import { useAuth } from '../context/AuthContext';
const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PRIORITY_STYLES = {
  CRITICAL: 'bg-red-950/40 border-red-700 text-red-300',
  HIGH: 'bg-orange-950/40 border-orange-700 text-orange-300',
  MEDIUM: 'bg-amber-950/30 border-amber-700 text-amber-300',
  LOW: 'bg-zinc-800 border-zinc-700 text-zinc-400',
};

const IMPACT_STYLES = {
  very_high: 'bg-red-900/40 text-red-300 border-red-700',
  high: 'bg-orange-900/40 text-orange-300 border-orange-700',
  medium: 'bg-amber-900/30 text-amber-300 border-amber-700',
  low: 'bg-zinc-800 text-zinc-400 border-zinc-700',
};

const RISK_STYLES = {
  high: 'text-red-400',
  medium: 'text-amber-400',
  low: 'text-green-400',
};

function RenderMd({ text }) {
  return (text || '').split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {i > 0 && <br />}
      {line.split(/(\*\*[^*]+\*\*)/).map((p, j) =>
        p.startsWith('**') && p.endsWith('**')
          ? <strong key={j} className="text-amber-400">{p.slice(2, -2)}</strong>
          : p
      )}
    </React.Fragment>
  ));
}

export default function ProcurementIntelligence() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('recommendations');
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [expandedRec, setExpandedRec] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/procurement-ai/dashboard`, { withCredentials: true });
        setData(res.data);
      } catch (e) { console.error(e); toast.error('Failed to load procurement data'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const askAI = async (e) => {
    e?.preventDefault();
    const q = aiQuestion.trim() || 'Give me a full procurement overview with priorities';
    setAiLoading(true);
    setAiResponse('');
    try {
      const res = await axios.post(`${API}/procurement-ai/ai-analysis`, { question: q }, { withCredentials: true });
      setAiResponse(res.data.analysis);
    } catch (e) { toast.error('AI analysis failed'); }
    finally { setAiLoading(false); }
  };

  const quickQuestions = [
    'What should I order this week?',
    'Which imported materials need urgent procurement?',
    'How are festivals affecting demand in the next 2 months?',
    'Which clients are likely to order soon?',
    'Is any material at risk due to geopolitical issues?',
  ];

  if (loading) return <div className="text-zinc-400 text-sm animate-pulse py-8">Analyzing procurement data...</div>;
  if (!data) return <div className="text-zinc-500 text-sm py-8">No procurement data available</div>;

  const m = data.metrics;

  return (
    <div className="space-y-4" data-testid="procurement-intelligence">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-semibold text-zinc-50 flex items-center gap-2">
          <Zap size={22} className="text-amber-400" /> AI Procurement Intelligence
        </h1>
        <p className="text-zinc-500 text-sm mt-0.5">Smart ordering engine — production patterns, festivals, import risks, client analysis</p>
      </div>

      {/* iter170 — surface per-row issues so user can fix bad records */}
      <DataIssuesBanner
        issues={data.data_issues || []}
        warnings={data.warnings || []}
        testId="proc-data-issues"
      />

      {/* Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {[
          { label: 'Critical', value: m.critical_orders, cls: 'border-red-700 text-red-400' },
          { label: 'High Priority', value: m.high_priority, cls: 'border-orange-700 text-orange-400' },
          { label: 'Medium', value: m.medium_priority, cls: 'border-amber-700 text-amber-400' },
          { label: 'Total Alerts', value: m.total_recommendations, cls: 'border-zinc-700 text-zinc-300' },
          { label: 'Import Risk', value: m.imported_at_risk, cls: 'border-red-700 text-red-400' },
          { label: 'Festivals', value: m.upcoming_festivals_count, cls: 'border-purple-700 text-purple-400' },
          { label: 'Clients', value: m.active_clients, cls: 'border-cyan-700 text-cyan-400' },
        ].map(c => (
          <div key={c.label} className={`bg-zinc-900 border rounded-xl p-3 text-center ${c.cls}`}>
            <div className="text-xl font-heading font-bold">{c.value}</div>
            <div className="text-[10px] mt-0.5 opacity-70">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1 overflow-x-auto max-w-full flex-nowrap">
        {[
          ['recommendations', 'Order Now', AlertTriangle],
          ['pending', 'Pending Orders', ShoppingBag],
          ['production', 'Production Needs', Package],
          ['festivals', 'Festival Calendar', Calendar],
          ['imports', 'Import Risks', Globe],
          ['clients', 'Client Patterns', Users],
          ...(isAdmin ? [['ai', 'Ask AI', Zap]] : []),
        ].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`proc-tab-${k}`}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${tab === k ? 'bg-amber-500 text-black font-semibold' : 'text-zinc-400 hover:text-zinc-200'}`}>
            <Icon size={12} />{l}
          </button>
        ))}
      </div>

      {/* ══ RECOMMENDATIONS ══ */}
      {tab === 'recommendations' && (
        <div className="space-y-2">
          {data.recommendations.length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center text-zinc-500 text-sm">All stock levels healthy. No procurement actions needed.</div>
          ) : data.recommendations.map((r, i) => (
            <div key={i} className={`border rounded-xl overflow-hidden ${PRIORITY_STYLES[r.priority]}`}
              data-testid={`rec-${i}`}>
              <div className="flex items-center justify-between px-4 py-3 cursor-pointer" onClick={() => setExpandedRec(expandedRec === i ? null : i)}>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${PRIORITY_STYLES[r.priority]}`}>{r.priority}</span>
                  <span className="text-sm font-semibold"><ItemCodeLabel name={r.material_name} /></span>
                  {r.imported && <Globe size={12} className="text-blue-400" title="Imported" />}
                  {r.festival && <Calendar size={12} className="text-purple-400" title="Festival" />}
                </div>
                <div className="flex items-center gap-3 text-xs">
                  {r.order_by && <span className="font-mono text-zinc-400">Order by: {r.order_by}</span>}
                  {expandedRec === i ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </div>
              </div>
              <div className="px-4 pb-3 text-xs">{r.reason}</div>
              {expandedRec === i && !r.festival && (
                <div className="px-4 pb-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs border-t border-current/10 pt-2">
                  <div><span className="text-zinc-500 block">Stock</span><span className="font-mono font-semibold">{r.current_stock} kg</span></div>
                  <div><span className="text-zinc-500 block">Reorder</span><span className="font-mono">{r.reorder_level} kg</span></div>
                  <div><span className="text-zinc-500 block">Weekly Use</span><span className="font-mono">{r.weekly_usage} kg</span></div>
                  <div><span className="text-zinc-500 block">Recommended</span><span className="font-mono font-bold text-amber-400">{r.recommended_qty} kg</span></div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ══ PRODUCTION NEEDS ══ */}
      {tab === 'production' && (
        <>
          <ActionPlanPanel />
          <DataGrid
            testId="production-needs"
            title="Material Consumption Detail"
            subtitle="Per-material 30-day consumption, pending demand, planned demand, and price trend"
            icon={<Package size={14} className="text-amber-400" />}
            rows={data.weekly_production_needs}
            rowKey={(n, i) => n.material_id || i}
            initialSort={{ key: 'weekly_avg', dir: 'desc' }}
            exportName="weekly-production-needs"
            emptyText="No recent production data to analyze"
            columns={[
            { key: 'material_name', label: 'Material', type: 'text',
              accessor: n => n.material_name,
              render: n => <span className="text-zinc-200 text-xs font-medium"><ItemCodeLabel name={n.material_name} /></span> },
            { key: 'weekly_avg', label: 'Weekly Avg (kg)', type: 'number', align: 'right',
              accessor: n => n.weekly_avg,
              render: n => <span className="font-mono text-amber-400 text-xs font-semibold">{n.weekly_avg}</span> },
            { key: 'total_30d', label: '30-Day (kg)', type: 'number', align: 'right',
              accessor: n => n.total_30d || 0,
              render: n => <span className="font-mono text-zinc-300 text-xs">{(n.total_30d ?? 0).toFixed(1)}</span> },
            { key: 'pending_orders_kg', label: 'Pending Orders (kg)', type: 'number', align: 'right',
              accessor: n => n.pending_orders_kg || 0,
              render: n => (n.pending_orders_kg || 0) > 0
                ? <span className="font-mono text-cyan-300 text-xs font-semibold" title={`₹${(n.pending_orders_amount || 0).toLocaleString('en-IN')} pipeline`}>{n.pending_orders_kg}</span>
                : <span className="text-zinc-700 text-xs">—</span> },
            { key: 'planned_batches_kg', label: 'Planned (kg)', type: 'number', align: 'right',
              accessor: n => n.planned_batches_kg || 0,
              render: n => (n.planned_batches_kg || 0) > 0
                ? <span className="font-mono text-violet-300 text-xs">{n.planned_batches_kg}</span>
                : <span className="text-zinc-700 text-xs">—</span> },
            { key: 'price_trend', label: 'Price Trend', type: 'enum',
              accessor: n => n.price_trend || 'stable',
              render: n => {
                const t = n.price_trend || 'stable';
                const pct = n.price_trend_pct || 0;
                if (t === 'rising') return <span className="inline-flex items-center gap-0.5 text-red-400 text-xs font-semibold"><ArrowUp size={9}/>+{pct}%</span>;
                if (t === 'falling') return <span className="inline-flex items-center gap-0.5 text-green-400 text-xs font-semibold"><ArrowDown size={9}/>{pct}%</span>;
                return <span className="inline-flex items-center gap-0.5 text-zinc-500 text-xs"><Minus size={9}/>stable</span>;
              } },
            { key: 'batch_count', label: 'Batches/30d', type: 'number', align: 'right',
              accessor: n => n.batch_count,
              render: n => <span className="font-mono text-zinc-400 text-xs">{n.batch_count}</span> },
          ]}
        />
        </>
      )}

      {/* ══ PENDING ORDERS — iter172 ══ */}
      {tab === 'pending' && <PendingOrdersPanel />}

      {/* ══ FESTIVAL CALENDAR ══ */}
      {tab === 'festivals' && (
        <div className="space-y-2">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <h3 className="text-zinc-200 text-sm font-semibold mb-1">Indian Festival Calendar — Demand Spike Prediction</h3>
            <p className="text-zinc-500 text-xs">High-impact festivals increase spice demand by 2-5x. Order 2-3 weeks before.</p>
          </div>
          {data.upcoming_festivals.length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center text-zinc-500 text-sm">No major festivals in the next 60 days</div>
          ) : data.upcoming_festivals.map((f, i) => {
            const daysUntil = Math.ceil((new Date(f.date) - new Date()) / 86400000);
            return (
              <div key={i} className={`border rounded-xl px-4 py-3 ${IMPACT_STYLES[f.impact]}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar size={14} />
                    <div>
                      <span className="text-sm font-semibold">{f.name}</span>
                      <span className="text-xs ml-2 opacity-60">({f.date})</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${IMPACT_STYLES[f.impact]}`}>{f.impact}</span>
                    <span className="font-mono text-xs">{daysUntil}d away</span>
                  </div>
                </div>
                {f.spice_demand?.length > 0 && (
                  <div className="mt-2 flex gap-1 flex-wrap">
                    {f.spice_demand.map(s => (
                      <span key={s} className="text-[10px] bg-black/20 px-1.5 py-0.5 rounded border border-current/20">{s}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {/* Full calendar */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <h4 className="text-zinc-400 text-xs font-semibold mb-2">Full Year Calendar</h4>
            <FullFestivalList />
          </div>
        </div>
      )}

      {/* ══ IMPORT RISKS ══ */}
      {tab === 'imports' && (
        <div className="space-y-2">
          <div className="bg-red-950/20 border border-red-800/40 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <ShieldAlert size={16} className="text-red-400" />
              <h3 className="text-red-300 text-sm font-semibold">Import Risk Assessment</h3>
            </div>
            <p className="text-red-400/60 text-xs">Imported materials with longer lead times and supply chain risks. Order well in advance.</p>
          </div>
          <DataGrid
            testId="import-risks"
            title="Imported materials — supply chain risk"
            icon={<ShieldAlert size={14} className="text-red-400" />}
            rows={data.import_risks}
            rowKey={(r, i) => r.material_id || i}
            initialSort={{ key: 'lead_time_days', dir: 'desc' }}
            exportName="import-risks"
            emptyText="No imported materials flagged"
            columns={[
              { key: 'material_name', label: 'Material', type: 'text',
                accessor: r => r.material_name,
                render: r => <span className="text-zinc-200 text-xs font-medium"><ItemCodeLabel name={r.material_name} /></span> },
              { key: 'origin', label: 'Origin', type: 'enum',
                accessor: r => r.origin,
                render: r => <span className="text-xs flex items-center gap-1"><Globe size={10} className="text-blue-400" /> {r.origin}</span> },
              { key: 'lead_time_days', label: 'Lead Time', type: 'number', align: 'right',
                accessor: r => r.lead_time_days,
                render: r => <span className="font-mono text-amber-400 text-xs font-semibold">{r.lead_time_days}d</span> },
              { key: 'risk_level', label: 'Risk', type: 'enum',
                accessor: r => r.risk_level,
                render: r => <span className={`text-xs font-semibold ${RISK_STYLES[r.risk_level]}`}>{(r.risk_level || '').toUpperCase()}</span> },
              { key: 'current_stock', label: 'Stock', type: 'number', align: 'right',
                accessor: r => r.current_stock,
                render: r => <span className="font-mono text-zinc-300 text-xs">{r.current_stock} kg</span> },
              { key: 'stock_days', label: 'Days of Stock', type: 'number', align: 'right',
                accessor: r => r.stock_days,
                render: r => <span className={`font-mono text-xs ${r.stock_days < 14 ? 'text-red-400 font-bold' : r.stock_days < 30 ? 'text-amber-400' : 'text-green-400'}`}>{r.stock_days}d</span> },
              { key: 'notes', label: 'Notes', type: 'text', filterable: false,
                accessor: r => r.notes,
                render: r => <span className="text-zinc-500 text-xs">{r.notes}</span> },
            ]}
          />
        </div>
      )}

      {/* ══ CLIENT PATTERNS ══ */}
      {tab === 'clients' && (
        <DataGrid
          testId="client-patterns"
          title="Client Ordering Patterns"
          subtitle="Historical analysis of ordering frequency and product preferences"
          icon={<Users size={14} className="text-cyan-400" />}
          rows={data.client_patterns}
          rowKey={(c, i) => c.client || i}
          initialSort={{ key: 'total_value', dir: 'desc' }}
          exportName="client-patterns"
          emptyText="No client data"
          columns={[
            { key: 'client', label: 'Client', type: 'text',
              accessor: c => c.client,
              render: c => <span className="text-zinc-200 text-xs font-medium">{c.client}</span> },
            { key: 'orders', label: 'Orders', type: 'number', align: 'right',
              accessor: c => c.orders,
              render: c => <span className="font-mono text-zinc-300 text-xs">{c.orders}</span> },
            { key: 'frequency', label: 'Frequency', type: 'enum',
              accessor: c => c.frequency,
              render: c => (
                <span className={`text-xs px-2 py-0.5 rounded-full border ${
                  c.frequency === 'weekly' ? 'bg-green-900/40 text-green-300 border-green-700' :
                  c.frequency === 'biweekly' ? 'bg-blue-900/40 text-blue-300 border-blue-700' :
                  c.frequency === 'monthly' ? 'bg-amber-900/30 text-amber-300 border-amber-700' :
                  'bg-zinc-800 text-zinc-400 border-zinc-700'
                }`}>{c.frequency}</span>
              ) },
            { key: 'total_value', label: 'Total Value', type: 'number', align: 'right',
              accessor: c => c.total_value,
              render: c => <span className="font-mono text-amber-400 text-xs">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(c.total_value)}</span> },
            { key: 'top_products', label: 'Top Products', type: 'text', sortable: false,
              accessor: c => (c.top_products || []).map(p => p.product).join(', '),
              render: c => <span className="text-zinc-400 text-xs">{(c.top_products || []).map(p => p.product).join(', ').slice(0, 60)}</span> },
            { key: 'last_order', label: 'Last Order', type: 'date',
              accessor: c => c.last_order || '',
              render: c => <span className="font-mono text-zinc-500 text-xs">{c.last_order || '—'}</span> },
          ]}
        />
      )}

      {/* ══ ASK AI ══ */}
      {tab === 'ai' && isAdmin && (
        <div className="space-y-3">
          <div className="bg-gradient-to-br from-amber-500/5 to-cyan-500/5 border border-amber-500/30 rounded-xl p-4">
            <h3 className="text-zinc-200 text-sm font-semibold mb-2 flex items-center gap-2">
              <Zap size={14} className="text-amber-400" /> AI Procurement Advisor
            </h3>
            <p className="text-zinc-500 text-xs mb-2">
              Ask anything about procurement, ordering, supply chain, or stocking depth. The advisor sees your live data feed:
            </p>
            <div className="flex flex-wrap gap-1 mb-3">
              {[
                'Lot turnaround per material',
                '180-day price trends',
                'In-transit POs (transport buffer)',
                'Weekly consumption rate',
                'Sales pipeline + client cadence',
                'Festival demand spikes',
                'Macro / micro / geopolitics',
                'Good supplier suggestions',
              ].map(t => (
                <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-900 text-zinc-400 border border-zinc-800">{t}</span>
              ))}
            </div>

            {/* Quick questions */}
            <div className="flex gap-1.5 flex-wrap mb-3">
              {quickQuestions.map(q => (
                <button key={q} onClick={() => { setAiQuestion(q); }}
                  className="text-[10px] px-2.5 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 rounded-lg border border-zinc-700 transition-colors">
                  {q}
                </button>
              ))}
            </div>

            <form onSubmit={askAI} className="flex gap-2">
              <input value={aiQuestion} onChange={e => setAiQuestion(e.target.value)}
                placeholder="e.g., How much black pepper should I order this week given my consumption + Vietnam supply?"
                className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
              <button type="submit" disabled={aiLoading}
                className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm rounded-xl disabled:opacity-50 flex items-center gap-2">
                {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Analyze
              </button>
            </form>
          </div>

          {aiLoading && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 flex items-center gap-3">
              <Loader2 size={16} className="text-amber-400 animate-spin" />
              <span className="text-zinc-400 text-sm">Analyzing procurement data with AI...</span>
            </div>
          )}

          {aiResponse && !aiLoading && (
            <div className="bg-zinc-900 border border-amber-800/30 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Zap size={14} className="text-amber-400" />
                <span className="text-amber-400 text-xs font-semibold">AI Procurement Analysis</span>
              </div>
              <div className="text-zinc-300 text-sm leading-relaxed">
                <RenderMd text={aiResponse} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FullFestivalList() {
  const [expanded, setExpanded] = useState(false);
  const festivals = [
    { name: 'Makar Sankranti', date: '2026-01-14', impact: 'medium' },
    { name: 'Holi', date: '2026-03-17', impact: 'high' },
    { name: 'Eid ul-Fitr', date: '2026-03-20', impact: 'high' },
    { name: 'Eid ul-Adha', date: '2026-05-27', impact: 'high' },
    { name: 'Raksha Bandhan', date: '2026-08-12', impact: 'medium' },
    { name: 'Ganesh Chaturthi', date: '2026-09-01', impact: 'high' },
    { name: 'Navratri', date: '2026-10-09', impact: 'high' },
    { name: 'Dussehra', date: '2026-10-18', impact: 'high' },
    { name: 'Diwali', date: '2026-11-06', impact: 'very_high' },
    { name: 'Christmas', date: '2026-12-25', impact: 'medium' },
  ];
  const shown = expanded ? festivals : festivals.slice(0, 5);
  return (
    <div>
      <div className="space-y-1">
        {shown.map((f, i) => (
          <div key={i} className="flex items-center justify-between text-xs py-1">
            <span className="text-zinc-300">{f.name}</span>
            <div className="flex items-center gap-2">
              <span className={`px-1.5 py-0.5 rounded text-[10px] border ${IMPACT_STYLES[f.impact]}`}>{f.impact}</span>
              <span className="font-mono text-zinc-500">{f.date}</span>
            </div>
          </div>
        ))}
      </div>
      <button onClick={() => setExpanded(!expanded)} className="text-amber-400 text-xs mt-2 hover:underline">
        {expanded ? 'Show less' : `Show all ${festivals.length}`}
      </button>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
//  iter172 — Pending Orders Panel
// ─────────────────────────────────────────────────────────────────────────
function PendingOrdersPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/procurement-ai/pending-orders`, { withCredentials: true });
        setData(r.data);
      } catch (e) { toast.error('Failed to load pending orders'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || !data) return <div className="text-zinc-500 text-sm py-8 text-center">Loading pending orders…</div>;
  const s = data.summary;
  const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

  return (
    <div className="space-y-3" data-testid="pending-orders-panel">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        <Stat label="Open Orders"      val={s.open_orders}      color="text-zinc-100" testId="pen-open-orders" />
        <Stat label="Line Items"       val={s.open_line_items}  color="text-zinc-100" testId="pen-line-items" />
        <Stat label="Total Qty (kg)"   val={s.total_qty_kg.toLocaleString('en-IN')} color="text-amber-300" testId="pen-qty" />
        <Stat label="Pipeline ₹"       val={fmt(s.total_amount)} color="text-emerald-300" testId="pen-amt" />
        <Stat label="Unique Clients"   val={s.unique_clients}   color="text-sky-300" testId="pen-clients" />
        <Stat label="Overdue"          val={s.overdue}          color={s.overdue > 0 ? 'text-red-400' : 'text-zinc-500'} testId="pen-overdue" hot={s.overdue > 0} />
        <Stat label="Due This Week"    val={s.due_this_week}    color={s.due_this_week > 0 ? 'text-amber-400' : 'text-zinc-500'} testId="pen-week" />
      </div>

      <DataGrid
        testId="pending-orders"
        title="Pending Sales Orders"
        subtitle="Not-yet-dispatched orders driving production + cash forecasting"
        icon={<ShoppingBag size={14} className="text-cyan-400" />}
        rows={data.rows}
        rowKey={(r, i) => `${r.order_id}-${i}`}
        initialSort={{ key: 'days_remaining', dir: 'asc' }}
        pageSize={50}
        exportName="pending-orders"
        emptyText="No pending sales orders"
        columns={[
          { key: 'order_no', label: 'Order No.', type: 'text',
            accessor: r => r.order_no,
            render: r => <span className="font-mono text-amber-400 text-xs">{r.order_no}</span> },
          { key: 'client', label: 'Client', type: 'text',
            accessor: r => r.client,
            render: r => (
              <DrillCell drill={{
                type: 'client-orders',
                params: { client: r.client, pending_only: false, months: 12 },
                title: `All orders — ${r.client}`,
              }} testId={`drill-pending-client-${r.client}`}>
                <span className="text-zinc-100 text-xs font-medium">{r.client}</span>
              </DrillCell>
            ) },
          { key: 'product', label: 'Product', type: 'text',
            accessor: r => r.product,
            render: r => <span className="text-zinc-300 text-xs">{r.product}</span> },
          { key: 'qty', label: 'Qty', type: 'number', align: 'right',
            accessor: r => r.qty,
            render: r => <span className="font-mono text-zinc-200 text-xs">{r.qty} <span className="text-zinc-500">{r.unit}</span></span> },
          { key: 'amount', label: 'Amount', type: 'number', align: 'right',
            accessor: r => r.amount,
            render: r => <span className="font-mono text-emerald-300 text-xs font-semibold">{fmt(r.amount)}</span> },
          { key: 'due_date', label: 'Due Date', type: 'date',
            accessor: r => r.due_date || '',
            render: r => <span className="font-mono text-zinc-400 text-xs">{r.due_date || '—'}</span> },
          { key: 'days_remaining', label: 'Days', type: 'number', align: 'right',
            accessor: r => r.days_remaining ?? 999,
            render: r => {
              if (r.days_remaining == null) return <span className="text-zinc-700 text-xs">—</span>;
              if (r.days_remaining < 0) return <span className="text-red-400 text-xs font-bold">{r.days_remaining}d ⚠</span>;
              if (r.days_remaining <= 7) return <span className="text-amber-400 text-xs font-semibold">{r.days_remaining}d</span>;
              return <span className="text-green-400 text-xs">{r.days_remaining}d</span>;
            } },
          { key: 'status', label: 'Status', type: 'enum',
            accessor: r => r.status,
            render: r => <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700">{r.status}</span> },
          { key: 'payment_status', label: 'Payment', type: 'enum',
            accessor: r => r.payment_status,
            render: r => {
              const cls = r.payment_status === 'Paid' ? 'bg-green-900/40 text-green-300 border-green-700'
                : r.payment_status === 'Partial' ? 'bg-amber-900/30 text-amber-300 border-amber-700'
                : 'bg-red-900/30 text-red-300 border-red-700';
              return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${cls}`}>{r.payment_status}</span>;
            } },
        ]}
      />
    </div>
  );
}

function Stat({ label, val, color, hot, testId }) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${hot ? 'border-red-700/60 bg-red-950/20' : 'border-zinc-800 bg-zinc-900/60'}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-base font-bold mt-0.5 ${color || 'text-zinc-100'}`}>{val}</div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
//  iter173 — Action Plan Panel ("AI Manager" buy-list)
// ─────────────────────────────────────────────────────────────────────────
const URGENCY_STYLE = {
  overdue: { dot: 'bg-red-500',    text: 'text-red-300',    border: 'border-red-700',  bg: 'bg-red-950/40' },
  urgent:  { dot: 'bg-amber-500',  text: 'text-amber-300',  border: 'border-amber-700', bg: 'bg-amber-950/40' },
  soon:    { dot: 'bg-yellow-400', text: 'text-yellow-200', border: 'border-yellow-700/50', bg: 'bg-yellow-950/30' },
  normal:  { dot: 'bg-zinc-500',   text: 'text-zinc-300',   border: 'border-zinc-700',  bg: 'bg-zinc-900/40' },
};

function ActionPlanPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [safetyWeeks, setSafetyWeeks] = useState(2);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/procurement-ai/action-plan?safety_weeks=${safetyWeeks}`, { withCredentials: true });
        setData(r.data);
      } catch { toast.error('Failed to load action plan'); }
      finally { setLoading(false); }
    })();
  }, [safetyWeeks]);

  if (loading || !data) return <div className="text-zinc-500 text-sm py-4 text-center">Computing action plan…</div>;
  const s = data.summary;
  const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

  return (
    <div className="space-y-3" data-testid="action-plan-panel">
      {/* Hero summary strip */}
      <div className="bg-gradient-to-br from-amber-950/50 to-zinc-900 border border-amber-700/40 rounded-xl p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="flex items-center gap-2">
              <Zap className="text-amber-400" size={16}/>
              <h3 className="text-zinc-100 text-base font-bold">AI Manager — Action Plan</h3>
            </div>
            <p className="text-zinc-400 text-xs mt-0.5">
              Auto-computed from current stock, 30-day consumption, pending sales orders, planned batches, lead times, and price trends. Updates on every page load.
            </p>
          </div>
          <div className="flex items-center gap-2 text-[11px]">
            <span className="text-zinc-500">Safety stock:</span>
            {[1, 2, 4].map(w => (
              <button key={w} onClick={() => setSafetyWeeks(w)}
                data-testid={`safety-weeks-${w}`}
                className={`px-2 py-0.5 rounded border ${safetyWeeks === w ? 'bg-amber-500 text-black border-amber-500 font-semibold' : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-700'}`}>
                {w}wk
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          <Stat label="To Order"        val={s.materials_to_order}        color="text-amber-300" testId="ap-to-order" />
          <Stat label="Shortage (kg)"   val={s.total_shortage_kg.toLocaleString('en-IN')} color="text-cyan-300" testId="ap-shortage" />
          <Stat label="Est. Cost"       val={fmt(s.total_est_cost)}       color="text-emerald-300" testId="ap-cost" />
          <Stat label="Clients Waiting" val={s.affected_clients}          color="text-sky-300" testId="ap-clients" />
          <Stat label="Overdue"         val={s.overdue}                   color={s.overdue > 0 ? 'text-red-400' : 'text-zinc-500'} hot={s.overdue > 0} testId="ap-overdue" />
          <Stat label="Urgent (≤3d)"    val={s.urgent}                    color={s.urgent > 0 ? 'text-amber-400' : 'text-zinc-500'} testId="ap-urgent" />
          <Stat label="Soon (≤7d)"      val={s.soon}                      color={s.soon > 0 ? 'text-yellow-400' : 'text-zinc-500'} testId="ap-soon" />
        </div>
      </div>

      {/* Action Plan Grid */}
      <DataGrid
        testId="action-plan"
        title="What to Order Right Now"
        subtitle="Sorted by urgency · click any column to sort, filter, export"
        icon={<ShoppingBag size={14} className="text-amber-400" />}
        rows={data.rows}
        rowKey={(r, i) => r.material_id || i}
        initialSort={null}
        exportName="action-plan"
        pageSize={50}
        emptyText="Nothing to order — stock + planned production covers all pending demand."
        columns={[
          { key: 'urgency', label: 'Urg', type: 'enum',
            accessor: r => r.urgency,
            render: r => {
              const st = URGENCY_STYLE[r.urgency] || URGENCY_STYLE.normal;
              return <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${st.border} ${st.bg} ${st.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}/>{r.urgency}
              </span>;
            } },
          { key: 'material_name', label: 'Material', type: 'text',
            accessor: r => r.material_name,
            render: r => (
              <DrillCell drill={{
                type: 'material-batches',
                params: { material_id: r.material_id, days: 30 },
                title: `Recent batches — ${r.material_name}`,
              }} testId={`drill-ap-mat-${r.material_id}`}>
                <span className="text-zinc-100 text-xs font-medium"><ItemCodeLabel name={r.material_name}/></span>
              </DrillCell>
            ) },
          { key: 'current_stock', label: 'Stock', type: 'number', align: 'right',
            accessor: r => r.current_stock,
            render: r => <span className="font-mono text-zinc-300 text-xs">{r.current_stock} kg</span> },
          { key: 'total_demand_30d', label: '30d Demand', type: 'number', align: 'right',
            accessor: r => r.total_demand_30d,
            render: r => (
              <div className="text-right">
                <div className="font-mono text-zinc-200 text-xs">{r.total_demand_30d} kg</div>
                <div className="text-[9px] text-zinc-500">
                  {r.consumption_30d}c · {r.pending_orders_kg}p · {r.planned_batches_kg}b
                </div>
              </div>
            ) },
          { key: 'shortage_kg', label: 'Short', type: 'number', align: 'right',
            accessor: r => r.shortage_kg,
            render: r => r.shortage_kg > 0
              ? <span className="font-mono text-red-300 text-xs font-bold">−{r.shortage_kg} kg</span>
              : <span className="font-mono text-zinc-500 text-xs">0</span> },
          { key: 'suggested_order_qty', label: 'Order', type: 'number', align: 'right',
            accessor: r => r.suggested_order_qty,
            render: r => <span className="font-mono text-amber-300 text-xs font-bold">{r.suggested_order_qty} kg</span> },
          { key: 'est_order_cost', label: 'Est. Cost', type: 'number', align: 'right',
            accessor: r => r.est_order_cost,
            render: r => r.est_order_cost > 0
              ? <span className="font-mono text-emerald-300 text-xs">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(r.est_order_cost)}</span>
              : <span className="text-zinc-700 text-xs">—</span> },
          { key: 'lead_time_days', label: 'Lead', type: 'number', align: 'right',
            accessor: r => r.lead_time_days,
            render: r => <span className="font-mono text-zinc-400 text-xs">{r.lead_time_days}d</span> },
          { key: 'order_by_date', label: 'Order By', type: 'date',
            accessor: r => r.order_by_date || '',
            render: r => {
              if (!r.order_by_date) return <span className="text-zinc-700 text-xs">—</span>;
              const st = URGENCY_STYLE[r.urgency] || URGENCY_STYLE.normal;
              return (
                <div>
                  <div className={`font-mono text-xs ${st.text} font-semibold`}>{r.order_by_date}</div>
                  {r.days_runway !== null && (
                    <div className="text-[9px] text-zinc-500">{r.days_runway < 0 ? `${Math.abs(r.days_runway)}d late` : `in ${r.days_runway}d`}</div>
                  )}
                </div>
              );
            } },
          { key: 'affected_clients_count', label: 'For Clients', type: 'number', align: 'left',
            accessor: r => r.affected_clients_count,
            render: r => r.affected_clients_count === 0
              ? <span className="text-zinc-700 text-xs">—</span>
              : (
                <div className="text-xs space-y-0.5">
                  <div className="text-sky-300 font-semibold">{r.affected_clients_count}</div>
                  <div className="text-[9px] text-zinc-500 truncate max-w-[160px]" title={r.affected_clients.join(', ')}>
                    {r.affected_clients.slice(0, 2).map((cname, i) => (
                      <React.Fragment key={cname}>
                        <DrillCell drill={{
                          type: 'client-orders',
                          params: { client: cname, pending_only: true },
                          title: `Pending orders — ${cname}`,
                        }} className="text-zinc-400 hover:text-amber-400" testId={`drill-ap-client-${cname}`}>
                          {cname}
                        </DrillCell>
                        {i < Math.min(r.affected_clients.length, 2) - 1 && ', '}
                      </React.Fragment>
                    ))}
                    {r.affected_clients.length > 2 && ` +${r.affected_clients.length - 2}`}
                  </div>
                </div>
              ) },
          { key: 'best_vendor', label: 'Best Vendor', type: 'text', filterable: false,
            accessor: r => r.best_vendor || '',
            render: r => r.best_vendor
              ? <div className="text-xs">
                  <div className="text-zinc-200 truncate max-w-[140px]" title={r.best_vendor}>{r.best_vendor}</div>
                  {r.best_vendor_rating && <div className="text-[9px] text-amber-400">★ {r.best_vendor_rating}</div>}
                </div>
              : <span className="text-zinc-700 text-xs">—</span> },
          { key: 'price_trend', label: 'Price', type: 'enum',
            accessor: r => r.price_trend || 'stable',
            render: r => {
              const t = r.price_trend || 'stable';
              const pct = r.price_trend_pct || 0;
              if (t === 'rising') return <span className="text-red-400 text-xs font-semibold">↑ +{pct}%</span>;
              if (t === 'falling') return <span className="text-green-400 text-xs font-semibold">↓ {pct}%</span>;
              return <span className="text-zinc-500 text-xs">—</span>;
            } },
        ]}
      />
    </div>
  );
}
